import { useState, useRef, useEffect, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { ApolloError } from '@apollo/client';
import debounce from 'lodash.debounce';
import {
  FollowUpTemplateDataFragment,
  FollowUpTemplateDataFragmentDoc,
  FollowUpTemplatesDocument,
  useSaveFollowUpTemplateMutation,
  useRenameFollowUpTemplateMutation,
  useDuplicateFollowUpTemplateMutation,
  useDeleteFollowUpTemplateMutation,
  useFollowUpTemplatesQuery,
  useFollowUpTemplateQuery,
  ProgramTagDataFragment,
} from '../../../../generated/graphql';
import { FollowUpForm, SavedActivityInputPreview } from '../../../lib/followUp';
import { savedActivityInputPreviewToActivityTemplateInput } from '../helpers';
import { formatProviderActivityForFollowUp } from '../../../lib/providerActivity';
import { customToast } from '../../../components/ToastAlert/customToast';
import { matchHomecomingError, HomecomingError } from '../../../../lib/errors';
import { AudioRecorderPlayerRef } from '../../../components/AudioRecorderPlayer';

const FORM_DEBOUNCE_MS = 400;
const FORM_CHANGES_STATE_DELAY_MS = 100;

interface UseFollowUpFormProps {
  programId: string;
  followUpTemplateId: string | null;
  initialActivities?: SavedActivityInputPreview[];
  initialProgramTags?: ProgramTagDataFragment[];
  audioRecorderPlayerRef: React.MutableRefObject<AudioRecorderPlayerRef | null>;
  setDraftedVoiceNoteMediaUrl: (url: string | undefined) => void;
  setEditorKey: React.Dispatch<React.SetStateAction<number>>;
}

export const useFollowUpForm = ({
  programId,
  followUpTemplateId,
  initialActivities,
  initialProgramTags,
  audioRecorderPlayerRef,
  setDraftedVoiceNoteMediaUrl,
  setEditorKey,
}: UseFollowUpFormProps) => {
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [currentDraftLastSavedAt, setCurrentDraftLastSavedAt] =
    useState<string>();
  const currentFollowUpTemplateDraftId = useRef<string>();

  const form = useForm<FollowUpForm>({
    mode: 'onSubmit',
    defaultValues: {
      subject: '',
      note: '',
      providerVoiceNoteId: null,
      activities: initialActivities ?? [],
      patientsOrTags: initialProgramTags ?? [],
    },
  });

  const {
    watch,
    getValues,
    reset: resetForm,
    formState: { isDirty },
  } = form;

  const [
    saveFollowUpTemplateMutation,
    { loading: saveFollowUpTemplateMutationLoading },
  ] = useSaveFollowUpTemplateMutation();
  const [renameFollowUpTemplateMutation] = useRenameFollowUpTemplateMutation();
  const [duplicateFollowUpTemplateMutation] =
    useDuplicateFollowUpTemplateMutation();
  const [deleteFollowUpTemplateMutation] = useDeleteFollowUpTemplateMutation();

  const {
    data: followUpTemplatesQueryData,
    refetch: refetchFollowUpTemplates,
  } = useFollowUpTemplatesQuery({
    variables: {
      input: {
        isDraft: false,
      },
    },
    fetchPolicy: 'network-only',
  });

  const { data: followUpDraftsQueryData, refetch: refetchFollowUpDrafts } =
    useFollowUpTemplatesQuery({
      variables: {
        input: {
          isDraft: true,
        },
      },
      fetchPolicy: 'network-only',
    });

  const followUpTemplates = (
    followUpTemplatesQueryData?.followUpTemplates ?? []
  ).sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  );

  const followUpDrafts = (
    followUpDraftsQueryData?.followUpTemplates ?? []
  ).sort(
    (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
  );

  const handleApplyFollowUpTemplate = async (
    followUpTemplate: FollowUpTemplateDataFragment,
  ) => {
    const patientsAndTags = followUpTemplate.isDraft
      ? [...followUpTemplate.patients, ...followUpTemplate.programTags]
      : getValues('patientsOrTags');

    const formattedActivities = followUpTemplate.activityTemplates.map(
      (activityTemplate) =>
        formatProviderActivityForFollowUp(
          activityTemplate.activity,
          1,
          activityTemplate.providerNote,
        ),
    );

    await audioRecorderPlayerRef.current?.resetRecorder();

    resetForm({
      patientsOrTags: patientsAndTags,
      subject: followUpTemplate.subject,
      note: followUpTemplate.providerNote,
      providerVoiceNoteId: followUpTemplate.providerVoiceNote?.id ?? null,
      activities: formattedActivities,
    });
    setDraftedVoiceNoteMediaUrl(followUpTemplate.providerVoiceNote?.media.url);
    setEditorKey((prev) => prev + 1);

    if (followUpTemplate.isDraft) {
      currentFollowUpTemplateDraftId.current = followUpTemplate.id;
      setCurrentDraftLastSavedAt(undefined);
      customToast.success('Draft applied.');
    } else {
      customToast.success('Template applied.');
    }
  };

  useFollowUpTemplateQuery({
    variables: {
      followUpTemplateId,
    },
    skip: !followUpTemplateId,
    onCompleted: (data) => {
      if (data?.followUpTemplate) {
        handleApplyFollowUpTemplate(data.followUpTemplate);
      }
    },
    onError: (error) => {
      customToast.error('Unable to fetch mail template.');
    },
  });

  const handleSaveFollowUpTemplate = async (
    name: string,
    followUpTemplateId?: string,
  ) => {
    try {
      const {
        subject,
        note: providerNote,
        providerVoiceNoteId,
        activities,
      } = getValues();
      const { data } = await saveFollowUpTemplateMutation({
        variables: {
          input: {
            id: followUpTemplateId,
            name,
            subject,
            isDraft: false,
            providerNote,
            providerVoiceNoteId,
            activityTemplates: activities.map((activity) =>
              savedActivityInputPreviewToActivityTemplateInput(activity),
            ),
          },
        },
        update: (cache, { data }) => {
          if (data?.saveFollowUpTemplate) {
            const savedFollowUpTemplate = data.saveFollowUpTemplate;

            cache.writeFragment({
              id: cache.identify(savedFollowUpTemplate),
              fragment: FollowUpTemplateDataFragmentDoc,
              fragmentName: 'followUpTemplateData',
              data: savedFollowUpTemplate,
            });

            cache.updateQuery(
              {
                query: FollowUpTemplatesDocument,
                variables: { input: { isDraft: false } },
              },
              (existingQueryData) => {
                if (!existingQueryData) return null;

                const updatedTemplates = followUpTemplateId
                  ? existingQueryData.followUpTemplates.map((template) =>
                      template.id === followUpTemplateId
                        ? savedFollowUpTemplate
                        : template,
                    )
                  : [
                      ...existingQueryData.followUpTemplates,
                      savedFollowUpTemplate,
                    ];

                return { followUpTemplates: updatedTemplates };
              },
            );
          }
        },
      });

      if (data?.saveFollowUpTemplate) {
        if (followUpTemplateId) {
          customToast.success('Mail template updated.');
        } else {
          customToast.success('New mail template saved.');
        }
      }
    } catch (err) {
      if (
        matchHomecomingError(
          err as ApolloError,
          HomecomingError.ResourceForbidden,
        )
      ) {
        // Do nothing
      } else {
        console.error('Error saving follow-up template:', err);
        customToast.error('Failed to save mail template.');
      }
      throw err;
    }
  };

  const handleSaveFollowUpDraft = useCallback(async () => {
    setIsSavingDraft(true);
    try {
      const followUpTemplateDraftId = currentFollowUpTemplateDraftId.current;
      const {
        patientsOrTags,
        subject,
        note: providerNote,
        providerVoiceNoteId,
        activities,
      } = getValues();
      const programIds = patientsOrTags
        .map((patientOrTag) => {
          return patientOrTag.__typename === 'PatientName'
            ? patientOrTag.programInstanceId
            : undefined;
        })
        .filter(Boolean) as string[];
      const programTagIds: string[] = [];
      const { data } = await saveFollowUpTemplateMutation({
        variables: {
          input: {
            id: followUpTemplateDraftId,
            programIds,
            programTagIds,
            subject,
            isDraft: true,
            providerNote,
            providerVoiceNoteId,
            activityTemplates: activities.map((activity) =>
              savedActivityInputPreviewToActivityTemplateInput(activity),
            ),
          },
        },
        update: (cache, { data }) => {
          if (data?.saveFollowUpTemplate) {
            const savedFollowUpTemplateDraft = data.saveFollowUpTemplate;

            cache.writeFragment({
              id: cache.identify(savedFollowUpTemplateDraft),
              fragment: FollowUpTemplateDataFragmentDoc,
              fragmentName: 'followUpTemplateData',
              data: savedFollowUpTemplateDraft,
            });

            cache.updateQuery(
              {
                query: FollowUpTemplatesDocument,
                variables: { input: { isDraft: true } },
              },
              (existingQueryData) => {
                if (!existingQueryData) return null;

                const updatedTemplateDrafts = followUpTemplateDraftId
                  ? existingQueryData.followUpTemplates.map((templateDraft) =>
                      templateDraft.id === followUpTemplateDraftId
                        ? savedFollowUpTemplateDraft
                        : templateDraft,
                    )
                  : [
                      ...existingQueryData.followUpTemplates,
                      savedFollowUpTemplateDraft,
                    ];

                return { followUpTemplates: updatedTemplateDrafts };
              },
            );
          }
        },
      });
      if (data?.saveFollowUpTemplate) {
        currentFollowUpTemplateDraftId.current = data.saveFollowUpTemplate.id;
        setCurrentDraftLastSavedAt(data.saveFollowUpTemplate.updatedAt);
      }
    } catch (err) {
      console.error('Error saving follow-up draft:', err);
    } finally {
      setIsSavingDraft(false);
    }
  }, [getValues, saveFollowUpTemplateMutation]);

  const debouncedSaveRef = useRef<ReturnType<typeof debounce>>();

  useEffect(() => {
    debouncedSaveRef.current = debounce(
      handleSaveFollowUpDraft,
      FORM_DEBOUNCE_MS,
      {
        leading: false,
        trailing: true,
      },
    );

    return () => {
      debouncedSaveRef.current?.cancel();
    };
  }, [handleSaveFollowUpDraft]);

  const debouncedSaveFollowUpDraft = useCallback(() => {
    debouncedSaveRef.current?.();
  }, []);

  const isDirtyRef = useRef(isDirty);
  useEffect(() => {
    isDirtyRef.current = isDirty;
  }, [isDirty]);

  useEffect(() => {
    const subscription = watch((value, { name, type }) => {
      setTimeout(() => {
        if (isDirtyRef.current) {
          debouncedSaveFollowUpDraft();
        }
      }, FORM_CHANGES_STATE_DELAY_MS);
    });
    return () => subscription.unsubscribe();
  }, [watch, debouncedSaveFollowUpDraft]);

  const handleRenameFollowUpTemplate = async (
    followUpTemplate: FollowUpTemplateDataFragment,
    newName: string,
  ) => {
    try {
      await renameFollowUpTemplateMutation({
        variables: { input: { id: followUpTemplate.id, name: newName } },
        update: (cache, { data }) => {
          cache.modify({
            id: cache.identify(data.renameFollowUpTemplate),
            fields: {
              name: () => data.renameFollowUpTemplate.name,
            },
          });
        },
      });
      customToast.success('Template renamed.');
    } catch (err) {
      console.error('Error renaming template:', err);
      customToast.error('Failed to rename template.');
    }
  };

  const handleDuplicateFollowUpTemplate = async (
    followUpTemplate: FollowUpTemplateDataFragment,
  ) => {
    try {
      await duplicateFollowUpTemplateMutation({
        variables: { input: { id: followUpTemplate.id } },
      });
      await refetchFollowUpTemplates();
      customToast.success('Template duplicated.');
    } catch (err) {
      console.error('Error duplicating template:', err);
      customToast.error('Failed to duplicate template.');
    }
  };

  const handleDeleteFollowUpTemplate = async (
    followUpTemplate: FollowUpTemplateDataFragment,
  ) => {
    try {
      await deleteFollowUpTemplateMutation({
        variables: { input: { id: followUpTemplate.id } },
      });
      if (followUpTemplate.isDraft) {
        await refetchFollowUpDrafts();
      } else {
        await refetchFollowUpTemplates();
      }
      customToast.success(
        `${followUpTemplate.isDraft ? 'Draft' : 'Template'} deleted.`,
      );
    } catch (err) {
      console.error('Error deleting template:', err);
      // Re-throw the error so parent can handle it (e.g., show in-use modal)
      throw err;
    }
  };

  return {
    form,
    isSavingDraft,
    currentDraftLastSavedAt,
    currentFollowUpTemplateDraftId,
    followUpTemplates,
    followUpDrafts,
    handleSaveFollowUpTemplate,
    handleApplyFollowUpTemplate,
    handleRenameFollowUpTemplate,
    handleDuplicateFollowUpTemplate,
    handleDeleteFollowUpTemplate,
    refetchFollowUpTemplates,
    refetchFollowUpDrafts,
    saveFollowUpTemplateMutationLoading,
  };
};
